import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DropdownButtonOption {
  label: string;
  value: string;
  onClick?: () => void;
  icon?: React.ReactNode;
}

interface DropdownButtonProps {
  options: DropdownButtonOption[];
  className?: string;
  dropdownClassName?: string;
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  children?: React.ReactNode;
}

const DropdownButton = ({
  options,
  className,
  dropdownClassName,
  variant = 'default',
  size = 'default',
  children,
}: DropdownButtonProps) => {
  const [open, setOpen] = useState(false);

  const handleButtonClick = () => {
    setOpen(!open);
  };

  return (
    <div className="relative">
      <Button
        variant={variant}
        size={size}
        className={cn('gap-2', className)}
        onClick={handleButtonClick}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {children}
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
        />
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-auto p-0">
          <div className={cn("bg-white border border-gray-200 rounded-md shadow-lg min-w-[120px]", dropdownClassName)}>
            <div className="flex flex-col">
              {options.map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    option.onClick?.();
                    setOpen(false);
                  }}
                  className={cn(
                    'px-3 py-2 text-sm text-left hover:bg-gray-100 transition-colors cursor-pointer',
                    'focus:outline-none focus:bg-gray-100',
                    'first:rounded-t-md last:rounded-b-md',
                    'border-0 bg-transparent w-full flex items-center gap-2'
                  )}
                >
                  {option.label}
                  {option.icon && (
                    <span className="flex-shrink-0">{option.icon}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DropdownButton.displayName = 'DropdownButton';

export { DropdownButton };
